import PropTypes from 'prop-types'
import React, { useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import _ from 'lodash'
import Helmet from 'react-helmet'
import { artistsStateSelector as stateSelector } from 'reduxModules/artists/selectors'
import { artistsMergeProps as mergeProps } from 'reduxModules/artists/dispatchers'
import { artistsFetchMore, artistsStatus } from 'reduxModules/artists'
import LoadMore from '../../generic/LoadMore'
import Artist from '../../generic/ProfileSummary'

import './styles.css'

export function ArtistList(props) {
  const { loadArtists, artists = [], nextArtists = null, requestStatus = null } = props

  const prevArtistsLengthRef = useRef(0)
  const prevStatusRef = useRef(requestStatus)

  useEffect(() => {
    // Use saved count from sessionStorage if returning to page, otherwise default
    const savedCount = sessionStorage.getItem('artistsScrollCount')
    const limit = savedCount ? parseInt(savedCount, 10) : 17
    loadArtists({ limit })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Restore scroll position when artists load
  useEffect(() => {
    const wasLoading = prevStatusRef.current !== artistsStatus.success
    const isLoaded = requestStatus === artistsStatus.success
    const moreLoaded = artists?.length > prevArtistsLengthRef.current

    if ((wasLoading && isLoaded) || moreLoaded) {
      const savedScrollY = sessionStorage.getItem('artistsScrollY')
      if (savedScrollY) {
        const targetY = parseInt(savedScrollY, 10)
        requestAnimationFrame(() => {
          const maxScroll = document.body.scrollHeight - window.innerHeight
          if (targetY <= maxScroll) {
            sessionStorage.removeItem('artistsScrollY')
            sessionStorage.removeItem('artistsScrollCount')
            window.scrollTo(0, targetY)
          } else if (nextArtists) {
            window.scrollTo(0, maxScroll)
          }
        })
      }
    }

    prevStatusRef.current = requestStatus
    prevArtistsLengthRef.current = artists?.length || 0
  }, [requestStatus, artists, nextArtists])

  // Save scroll position when navigating to artist detail
  useEffect(() => {
    return () => {
      const currentPath = window.location.pathname
      if (
        _.startsWith(currentPath, '/artists/') &&
        currentPath !== '/artists'
      ) {
        sessionStorage.setItem('artistsScrollY', window.scrollY.toString())
        sessionStorage.setItem('artistsScrollCount', artists?.length?.toString() || '17')
      }
    }
  }, [artists])

  return (
    <article id="ArtistList" className="wrapper">
      <Helmet>
        <title>Teachers | Stan Winston School of Character Arts</title>
        <meta
          name="description"
          content="Find the most talented professionals in the field of Special Effects Character Creation, Character Design & Makeup FX"
        />
        <meta
          property="og:title"
          content="Teachers | Stan Winston School of Character Arts"
        />
        <meta
          property="og:description"
          content="Find the most talented professionals in the field of Special Effects Character Creation, Character Design & Makeup FX"
        />
      </Helmet>

      <section className="heading">
        <h1>Teachers</h1>
      </section>

      <section className="artists-list-content">
        {artists.map((artist, idx) => (
          <Artist
            key={`artist-${idx}`}
            className="artist"
            elementName={`artists-${idx}`}
            name={artist.title}
            photo={artist.photo.boxThumb}
            slug={artist.slug}
          />
        ))}
      </section>

      {nextArtists && (
        <div className="load-more">
          <LoadMore
            fetchMoreAction={artistsFetchMore}
            next={nextArtists}
            listContainerId="ArtistList"
          />
        </div>
      )}
    </article>
  )
}

ArtistList.propTypes = {
  loadArtists: PropTypes.func.isRequired,
  artists: PropTypes.arrayOf(PropTypes.object),
  nextArtists: PropTypes.string,
  requestStatus: PropTypes.string
}

export default connect(stateSelector, null, mergeProps)(ArtistList)
